import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { TeamPanel } from "../components/TeamPanel";
import { ApiKeysPanel } from "../components/ApiKeysPanel";
import { TagsPanel } from "../components/TagsPanel";

const GRADE_COLORS = {
  good: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", dot: "bg-emerald-500" },
  warn: { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", dot: "bg-amber-500" },
  bad: { bg: "bg-rose-50", text: "text-rose-700", border: "border-rose-200", dot: "bg-rose-500" },
};

const GRADE_LABEL = { good: "Healthy", warn: "Watch", bad: "Critical" };

export default function SettingsPage() {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [seedsText, setSeedsText] = useState("");
  const [approvalText, setApprovalText] = useState("");
  const [topicsText, setTopicsText] = useState("");
  const [prospectKwText, setProspectKwText] = useState("");
  const [prospectExText, setProspectExText] = useState("");
  const [health, setHealth] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cleanBusy, setCleanBusy] = useState(false);
  const [cleanResult, setCleanResult] = useState(null);

  const load = () => api.settings().then(d => {
    setS(d);
    setSeedsText((d.seed_emails || []).join("\n"));
    setApprovalText((d.approval_recipients || []).join("\n"));
    setTopicsText((d.preference_topics || []).join(", "));
    setProspectKwText((d.prospect_keywords || []).join(", "));
    setProspectExText((d.prospect_exclusions || []).join(", "));
  });

  useEffect(() => {
    load();
    api.listHealth().then(setHealth);
    // eslint-disable-next-line
  }, []);

  if (!s) return <div className="text-sm text-zinc-500">Loading…</div>;

  const save = async () => {
    const updated = await api.updateSettings({
      from_name: s.from_name,
      from_email: s.from_email,
      reply_to: s.reply_to || "",
      default_footer: s.default_footer,
      company_address: s.company_address || "",
      footer_html: s.footer_html || "",
      max_per_minute: s.max_per_minute || 600,
      seed_emails: seedsText,
      approval_recipients: approvalText,
      approver_email: s.approver_email || "",
      brand_color: s.brand_color || "#005191",
      accent_color: s.accent_color || "#f5821f",
      logo_url: s.logo_url || "",
      hot_lead_alerts_enabled: !!s.hot_lead_alerts_enabled,
      weekly_digest_enabled: !!s.weekly_digest_enabled,
      lead_alert_recipients: s.lead_alert_recipients || "",
      utm_enabled: s.utm_enabled !== false,
      utm_source: s.utm_source || "medihub",
      preference_topics: topicsText,
      prospect_keywords: prospectKwText,
      prospect_exclusions: prospectExText,
    });
    setS({ ...updated, resend_configured: s.resend_configured, webhook_signature_configured: s.webhook_signature_configured });
    toast.success("Saved");
  };

  const cleanStale = async () => {
    if (!window.confirm(`Suppress all active contacts who haven't opened anything in 180+ days?`)) return;
    setBusy(true);
    try {
      const r = await api.suppressStale(180);
      toast.success(`${r.affected} contacts suppressed`);
      api.listHealth().then(setHealth);
    } finally { setBusy(false); }
  };

  const runDataClean = async () => {
    if (!window.confirm("Scan every contact and repair emails, links and names (removes invisible characters, mailto: prefixes, broken URL formats) and flag LinkedIn links that don't match the person's name?")) return;
    setCleanBusy(true);
    try {
      const r = await api.cleanContactData();
      setCleanResult(r);
      toast.success(`${r.contacts_fixed.toLocaleString()} contact${r.contacts_fixed === 1 ? "" : "s"} repaired`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cleanup failed");
    } finally { setCleanBusy(false); }
  };

  return (
    <div className="space-y-5 max-w-3xl">
      <div>
        <h2 className="font-heading text-2xl font-medium tracking-tight">Settings</h2>
        <p className="text-sm text-zinc-500 mt-0.5">Sender identity, deliverability, and compliance.</p>
      </div>

      {/* ===== List health card ===== */}
      {health && (
        <div className="rounded-lg border border-zinc-200 bg-white p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-base font-medium">List health</h3>
            <span className="text-xs text-zinc-500">{health.totals.sent.toLocaleString()} emails sent lifetime</span>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <HealthMetric label="Bounce rate" data={health.bounce} suffix={`${health.totals.bounced} bounced`} />
            <HealthMetric label="Spam complaints" data={health.complaint} suffix={`${health.totals.complaints} reports`} />
            <HealthMetric label="Unsubscribe rate" data={health.unsubscribe} suffix={`${health.totals.unsubscribed} unsubs`} />
          </div>
          <div className="mt-4 pt-4 border-t border-zinc-100 grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
            <Mini label="Total" value={health.contacts.total} />
            <Mini label="Active" value={health.contacts.active} />
            <Mini label="Bounced" value={health.contacts.bounced} />
            <Mini label="Unsubscribed" value={health.contacts.unsubscribed} />
            <Mini label="Stale (180d)" value={health.contacts.stale_180d} />
          </div>
          {health.contacts.stale_180d > 0 && (
            <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 flex items-center justify-between gap-3">
              <div className="text-xs text-amber-900">
                <strong>{health.contacts.stale_180d}</strong> active contact{health.contacts.stale_180d === 1 ? "" : "s"} haven't opened anything in 180+ days. Suppressing them improves your sender reputation.
              </div>
              <button data-testid="suppress-stale" disabled={busy} onClick={cleanStale} className="text-xs px-3 py-1.5 rounded-md bg-amber-900 text-white hover:bg-amber-950 whitespace-nowrap disabled:opacity-50">
                Suppress stale
              </button>
            </div>
          )}
        </div>
      )}

      {/* ===== Data quality ===== */}
      <div className="rounded-lg border border-zinc-200 bg-white p-5" data-testid="data-quality-card">
        <div className="flex items-center justify-between gap-3 mb-1">
          <h3 className="font-heading text-base font-medium">Data quality</h3>
          <button data-testid="clean-data-btn" disabled={cleanBusy} onClick={runDataClean} className="text-xs px-3 py-1.5 rounded-md bg-zinc-950 text-white hover:bg-zinc-800 whitespace-nowrap disabled:opacity-50">
            {cleanBusy ? "Cleaning…" : "Clean existing data"}
          </button>
        </div>
        <p className="text-xs text-zinc-500">
          Repairs data that arrived broken from imports: invisible characters in emails and names, <code>mailto:</code> prefixes,
          stray punctuation and malformed website/LinkedIn links. Also flags LinkedIn URLs that don't contain the person's name.
          Safe to run any time — clean records are left untouched, and email fixes that would collide with another contact are skipped and reported.
        </p>
        {cleanResult && (
          <div className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-3 text-sm" data-testid="clean-result">
            <Mini label="Scanned" value={cleanResult.scanned} />
            <Mini label="Contacts repaired" value={cleanResult.contacts_fixed} />
            <Mini label="Emails fixed" value={cleanResult.emails_fixed} />
            <Mini label="Links fixed" value={cleanResult.urls_fixed} />
            <Mini label="LinkedIn flagged" value={cleanResult.linkedin_flagged} />
          </div>
        )}
        {cleanResult?.email_conflicts?.length > 0 && (
          <p className="mt-2 text-[11px] text-amber-700">
            {cleanResult.email_conflicts.length} email{cleanResult.email_conflicts.length === 1 ? "" : "s"} couldn't be auto-fixed (a contact with the cleaned address already exists): {cleanResult.email_conflicts.slice(0, 5).join(", ")}
          </p>
        )}
        {cleanResult?.linkedin_flagged > 0 && (
          <a href="/contacts?has_linkedin=review" className="inline-block mt-2 text-[11px] text-sky-700 hover:underline" data-testid="review-flagged-link">
            Review the {cleanResult.linkedin_flagged.toLocaleString()} flagged LinkedIn link{cleanResult.linkedin_flagged === 1 ? "" : "s"} →
          </a>
        )}
      </div>

      {/* ===== Tags ===== */}
      <TagsPanel />

      {/* ===== Sender identity ===== */}
      <Section title="Sender identity" subtitle="What recipients see in their inbox. Reply-To is where their replies go — can be different from the From address.">
        <Field label="From name"><input data-testid="set-fromname" value={s.from_name || ""} onChange={e => setS({...s, from_name: e.target.value})} className="ch-input"/></Field>
        <Field label="From email"><input data-testid="set-fromemail" value={s.from_email || ""} onChange={e => setS({...s, from_email: e.target.value})} className="ch-input" placeholder="hello@mail.yourdomain.com"/></Field>
        <Field label="Default Reply-To for new campaigns" hint="Pre-fills the compulsory 'Replies go to' field on every new campaign (you confirm or change it per campaign). Several addresses separated by commas."><input data-testid="set-replyto" value={s.reply_to || ""} onChange={e => setS({...s, reply_to: e.target.value})} className="ch-input" placeholder="you@yourdomain.com, colleague@yourdomain.com"/></Field>
        <Field label="Brand colour (used by design blocks in the composer)">
          <div className="flex items-center gap-2">
            <input data-testid="set-brand-color" type="color" value={s.brand_color || "#005191"} onChange={e => setS({...s, brand_color: e.target.value})} className="h-9 w-14 rounded-md border border-zinc-200 cursor-pointer"/>
            <span className="text-sm font-mono text-zinc-600">{s.brand_color || "#005191"}</span>
          </div>
        </Field>
        <Field label="Accent colour (quotes, highlights)">
          <div className="flex items-center gap-2">
            <input data-testid="set-accent-color" type="color" value={s.accent_color || "#f5821f"} onChange={e => setS({...s, accent_color: e.target.value})} className="h-9 w-14 rounded-md border border-zinc-200 cursor-pointer"/>
            <span className="text-sm font-mono text-zinc-600">{s.accent_color || "#f5821f"}</span>
          </div>
        </Field>
        <Field label="Email logo (shown by the Logo block in campaigns)">
          <div className="flex items-center gap-3">
            {s.logo_url ? <img src={s.logo_url} alt="logo" className="h-10 max-w-[180px] object-contain border border-zinc-200 rounded bg-white p-1"/> : <span className="text-xs text-zinc-400">No logo yet</span>}
            <label className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-zinc-200 hover:bg-zinc-50 cursor-pointer" data-testid="set-logo-upload">
              <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                try { const r = await api.uploadImage(f); setS({ ...s, logo_url: r.url }); toast.success("Logo uploaded — remember to Save"); }
                catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
              }}/>
              Upload logo
            </label>
          </div>
        </Field>
      </Section>

      {/* ===== Compliance footer ===== */}
      <Section title="Compliance footer" subtitle="Auto-appended to every email above the unsubscribe link. Required by GDPR / CAN-SPAM.">
        <Field label="Company physical address">
          <input data-testid="set-address" value={s.company_address || ""} onChange={e => setS({...s, company_address: e.target.value})} className="ch-input" placeholder="123 Example Street, City, Country"/>
        </Field>
        <Field label="Custom footer HTML (optional)">
          <textarea data-testid="set-footer-html" value={s.footer_html || ""} onChange={e => setS({...s, footer_html: e.target.value})} rows={3} className="ch-input h-auto py-2" placeholder="<p>Sent by Acme Corp · Tax ID 12345</p>"/>
        </Field>
      </Section>

      {/* ===== Throughput ===== */}
      <Section title="Sending throughput" subtitle="Maximum emails per minute during a campaign. Lower this during reputation warm-up.">
        <Field label="Max emails per minute">
          <input data-testid="set-throughput" type="number" min="1" max="10000" value={s.max_per_minute || 600} onChange={e => setS({...s, max_per_minute: parseInt(e.target.value || "0", 10)})} className="ch-input w-32"/>
          <p className="text-[11px] text-zinc-500 mt-1">{Math.round((s.max_per_minute || 600) / 60)} per second · default is 600/min (10/sec). Lower to ~60–120/min during warm-up.</p>
        </Field>
      </Section>

      {/* ===== Seed list ===== */}
      <Section title="Seed test list" subtitle="Send any campaign to this list as a final pre-flight check. Add your own Gmail, Outlook, Yahoo etc. addresses to verify inbox placement across providers.">
        <Field label="Seed email addresses (one per line)">
          <textarea data-testid="set-seeds" value={seedsText} onChange={e => setSeedsText(e.target.value)} rows={4} className="ch-input h-auto py-2 font-mono text-[12.5px]" placeholder={"your@gmail.com\nyour@outlook.com\nyour@yahoo.com"}/>
        </Field>
      </Section>

      {/* ===== Campaign approval ===== */}
      <Section title="Campaign approval" subtitle="Every campaign must be emailed to these people as an [APPROVAL COPY] first. Only the approver's email contains the Approve button — the real send stays locked until they click it. Editing a campaign after approval resets the gate.">
        <Field label="Approval copy recipients (one per line)">
          <textarea data-testid="set-approval-recipients" value={approvalText} onChange={e => setApprovalText(e.target.value)} rows={3} className="ch-input h-auto py-2 font-mono text-[12.5px]" placeholder={"f.desbuquois@medicysltd.co.uk\nn.trowell@medicysltd.co.uk"}/>
        </Field>
        <Field label="Approver (the only person who can click Approve)">
          <input data-testid="set-approver" value={s.approver_email || ""} onChange={e => setS({...s, approver_email: e.target.value})} className="ch-input" placeholder="f.desbuquois@medicysltd.co.uk"/>
        </Field>
      </Section>

      {/* ===== UTM link tracking ===== */}
      <Section title="Link tracking (UTM)" subtitle="Every external link in your emails is automatically tagged with utm_source, utm_medium=email and utm_campaign=<campaign name> so Google Analytics can attribute website visits to your campaigns. Links that already carry UTM tags are left untouched.">
        <label className="flex items-center gap-2 text-sm">
          <input data-testid="utm-toggle" type="checkbox" checked={s.utm_enabled !== false} onChange={e => setS({ ...s, utm_enabled: e.target.checked })} />
          Add UTM tags to all external links at send time
        </label>
        <Field label="utm_source value">
          <input data-testid="utm-source" value={s.utm_source || "medihub"} onChange={e => setS({ ...s, utm_source: e.target.value })} className="ch-input w-56 font-mono text-[12.5px]" placeholder="medihub"/>
        </Field>
      </Section>

      {/* ===== Preference centre ===== */}
      <Section title="Preference centre topics" subtitle="Every email footer links to a personal preference page where contacts can choose email frequency (all / monthly / 90-day pause), untick topics, or unsubscribe. Give each campaign a topic in step 2 of the composer and topic opt-outs are respected automatically.">
        <Field label="Topics (comma-separated)">
          <input data-testid="pref-topics" value={topicsText} onChange={e => setTopicsText(e.target.value)} className="ch-input" placeholder="Industry reports, Product news, Events & webinars"/>
        </Field>
      </Section>

      {/* ===== Prospecting keywords ===== */}
      <Section title="Prospecting job-title keywords" subtitle="These appear as clickable chips on the Prospect page. Selected keywords are sent to Hunter WITH the search, so results are filtered by job title before credits are charged — you never pay for HR or IT people. Rank words like Director or Partner are best used on agencies and consultancies, not big pharma domains.">
        <Field label={`Keywords (comma-separated) — ${prospectKwText.split(",").map(t => t.trim()).filter(Boolean).length} in the list`}>
          <textarea data-testid="prospect-keywords" rows={5} value={prospectKwText} onChange={e => setProspectKwText(e.target.value)} className="ch-input !h-auto py-2 leading-relaxed" placeholder="Market research, HEOR, Patient, …"/>
        </Field>
        <Field label="Exclusion keywords (comma-separated) — anyone whose job title matches these is dropped from Hunter and Apollo results, even if they match a keyword above">
          <textarea data-testid="prospect-exclusions" rows={2} value={prospectExText} onChange={e => setProspectExText(e.target.value)} className="ch-input !h-auto py-2 leading-relaxed" placeholder="Clinical Research Associate, CRA"/>
        </Field>
      </Section>
      {/* ===== Lead alerts ===== */}
      <Section title="Lead alerts">
        <p className="text-xs text-zinc-500 -mt-1">Get notified the moment engagement scoring flags someone as a hot lead, plus an optional Monday-morning digest of your top 10 leads.</p>
        <label className="flex items-center gap-2 text-sm">
          <input data-testid="hot-alerts-toggle" type="checkbox" checked={!!s.hot_lead_alerts_enabled} onChange={e => setS({ ...s, hot_lead_alerts_enabled: e.target.checked })} />
          Email me when a contact becomes a hot lead (max once per contact per 30 days)
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input data-testid="weekly-digest-toggle" type="checkbox" checked={!!s.weekly_digest_enabled} onChange={e => setS({ ...s, weekly_digest_enabled: e.target.checked })} />
          Send a weekly top-10 leads digest (Mondays, 8am UTC)
        </label>
        <label className="block">
          <span className="block text-[11px] font-medium text-zinc-600 uppercase tracking-wider mb-1">Alert recipients (comma-separated)</span>
          <input data-testid="lead-alert-recipients" value={s.lead_alert_recipients || ""} onChange={e => setS({ ...s, lead_alert_recipients: e.target.value })} placeholder="f.desbuquois@medicysltd.co.uk, bd@medicysltd.co.uk" className="ch-input" />
        </label>
      </Section>

      {/* ===== Team (admin only) ===== */}
      {user?.role === "admin" && <TeamPanel />}

      {/* ===== API keys (admin only) ===== */}
      {user?.role === "admin" && <ApiKeysPanel />}

      {/* ===== Design migration ===== */}
      <Section title="Design migration (Preview ↔ Production)" subtitle="Move your design assets — images, brand kit, templates and draft campaigns — between environments. This tool NEVER reads or writes contacts, lists, segments or suppressions.">
        <div className="flex flex-wrap items-center gap-2">
          <button data-testid="export-design" onClick={async () => {
            try {
              const data = await api.exportDesign();
              const blob = new Blob([JSON.stringify(data)], { type: "application/json" });
              const a = document.createElement("a");
              a.href = URL.createObjectURL(blob);
              a.download = `contacthub-design-${new Date().toISOString().slice(0, 10)}.json`;
              a.click();
              URL.revokeObjectURL(a.href);
              toast.success(`Bundle downloaded — ${data.images.length} images, ${data.templates.length} templates, ${data.campaigns.length} draft campaigns`);
            } catch (e) { toast.error("Export failed"); }
          }} className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-md bg-zinc-950 text-white hover:bg-zinc-800">
            Download design bundle
          </button>
          <label className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-md border border-zinc-200 hover:bg-zinc-50 cursor-pointer" data-testid="import-design">
            <input type="file" accept=".json,application/json" className="hidden" onChange={async (e) => {
              const f = e.target.files?.[0];
              if (!f) return;
              try {
                const r = await api.importDesign(f);
                toast.success(`Imported: ${r.imported.images} images, ${r.imported.templates} templates, ${r.imported.campaigns} draft campaigns, ${r.imported.settings} brand settings. Skipped (already present): ${r.skipped.images} images, ${r.skipped.campaigns} campaigns.`, { duration: 8000 });
                load();
              } catch (err) {
                toast.error(err.response?.data?.detail || "Import failed");
              } finally { e.target.value = ""; }
            }}/>
            Import design bundle…
          </label>
        </div>
        <p className="text-[11px] text-zinc-500">How to migrate: open this page in <strong>Preview</strong> → Download. Then open this page in your <strong>Production</strong> app → Import the file. Image links are rewritten to the new environment automatically. Images and draft campaigns that already exist are skipped; templates and brand colours/logo are updated to match the bundle.</p>
      </Section>

      {/* ===== Provider status ===== */}
      <Section title="Provider status">
        <div className="space-y-2 text-sm">
          <ProviderRow label="Resend API key" ok={s.resend_configured} note={s.resend_configured ? "Configured" : "Not configured — sending is simulated. Add RESEND_API_KEY to backend env."}/>
          <ProviderRow label="Webhook signature" ok={s.webhook_signature_configured} note={s.webhook_signature_configured ? "Verified" : "Not configured — webhooks are accepted without verification. Add RESEND_WEBHOOK_SECRET to backend env."}/>
        </div>
        <div className="rounded-md border border-zinc-100 bg-zinc-50 p-3">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600 mb-1">Delivery confirmation webhook</div>
          <p className="text-xs text-zinc-600 mb-2">To get per-recipient <strong>delivered / bounced / marked-as-spam</strong> data, add this endpoint in your Resend dashboard (Webhooks → Add endpoint) and tick the email.delivered, email.bounced and email.complained events:</p>
          <div className="flex items-center gap-2">
            <code data-testid="webhook-url" className="flex-1 text-[12px] font-mono bg-white border border-zinc-200 rounded px-2 py-1.5 break-all select-all">{`${window.location.origin}/api/resend/webhook`}</code>
            <button data-testid="copy-webhook-url" onClick={async () => { await navigator.clipboard.writeText(`${window.location.origin}/api/resend/webhook`).catch(() => {}); toast.success("Webhook URL copied"); }} className="text-xs px-2.5 py-1.5 rounded-md border border-zinc-200 hover:bg-zinc-50 whitespace-nowrap">Copy</button>
          </div>
        </div>
      </Section>

      <div className="flex justify-end">
        <button data-testid="set-save" onClick={save} className="ch-btn-primary">Save settings</button>
      </div>

      <style>{`.ch-input{display:block;width:100%;height:36px;border-radius:6px;border:1px solid #e4e4e7;padding:0 10px;font-size:13px;background:white;color:#09090b}
      .ch-input:focus{outline:none;box-shadow:0 0 0 1px #09090b;border-color:#09090b}
      .ch-btn-primary{padding:8px 14px;font-size:13px;font-weight:500;background:#09090b;color:white;border-radius:6px}
      .ch-btn-primary:hover{background:#27272a}`}</style>
    </div>
  );
}

const Section = ({ title, subtitle, children }) => (
  <div className="rounded-lg border border-zinc-200 bg-white p-5 space-y-3">
    <div>
      <h3 className="font-heading text-base font-medium">{title}</h3>
      {subtitle && <p className="text-xs text-zinc-500 mt-0.5">{subtitle}</p>}
    </div>
    {children}
  </div>
);

const Field = ({ label, hint, children }) => (
  <label className="block">
    <span className="block text-[11px] font-medium text-zinc-600 uppercase tracking-wider mb-1">{label}</span>
    {children}
    {hint && <span className="block text-[11px] text-zinc-400 mt-1">{hint}</span>}
  </label>
);

const HealthMetric = ({ label, data, suffix }) => {
  const c = GRADE_COLORS[data.grade] || GRADE_COLORS.good;
  return (
    <div className={`rounded-md border ${c.border} ${c.bg} p-3`}>
      <div className="text-[11px] uppercase tracking-wider font-semibold text-zinc-600">{label}</div>
      <div className="flex items-baseline gap-1.5 mt-1.5">
        <span className={`font-heading text-2xl font-medium ${c.text}`}>{data.rate}%</span>
        <span className="text-[10px] text-zinc-500">good ≤ {data.threshold_good}% · watch ≤ {data.threshold_warn}%</span>
      </div>
      <div className="flex items-center gap-1.5 mt-1.5">
        <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
        <span className={`text-[11px] font-medium ${c.text}`}>{GRADE_LABEL[data.grade]}</span>
        <span className="text-[11px] text-zinc-500 ml-auto">{suffix}</span>
      </div>
    </div>
  );
};

const Mini = ({ label, value }) => (
  <div>
    <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold">{label}</div>
    <div className="font-heading text-lg font-medium text-zinc-900 mt-0.5">{(value || 0).toLocaleString()}</div>
  </div>
);

const ProviderRow = ({ label, ok, note }) => (
  <div className="flex items-center justify-between py-2 border-b border-zinc-100 last:border-b-0">
    <div>
      <div className="font-medium text-zinc-900">{label}</div>
      <div className="text-xs text-zinc-500">{note}</div>
    </div>
    <span className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-0.5 rounded-full border ${ok ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-amber-200 bg-amber-50 text-amber-700"}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${ok ? "bg-emerald-500" : "bg-amber-500"}`} />
      {ok ? "OK" : "Action needed"}
    </span>
  </div>
);
